/**
 * Format a Date object to HH:mm string format
 * @param {Date} date - The date object to format
 * @returns {string} Time in HH:mm format
 */
export const formatTimeToHHMM = (date) => {
  if (!date || !(date instanceof Date)) return "";
  const hours = date.getHours().toString().padStart(2, "0");
  const minutes = date.getMinutes().toString().padStart(2, "0");
  return `${hours}:${minutes}`;
};

/**
 * Parse time string (HH:mm) to Date object
 * @param {string} timeString - Time in HH:mm format
 * @returns {Date|null} Date object with the specified time
 */
export const parseTimeStringToDate = (timeString) => {
  if (!timeString || typeof timeString !== "string") return null;

  const [hours, minutes] = timeString.split(":");
  if (!hours || !minutes) return null;

  const date = new Date();
  date.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);

  return date;
};

/**
 * Convert schedule object from API format to component format
 * @param {Object|Array} apiSchedule - Schedule from API (can be old object format or new array format)
 * @param {boolean} useDefaultForEmpty - If true, returns INITIAL_WEEK_SCHEDULE when no data, otherwise returns empty object
 * @returns {Object} Schedule with Date objects
 */
export const convertAPIScheduleToComponentFormat = (apiSchedule, useDefaultForEmpty = true) => {
  const DAYS = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
  const componentSchedule = {};

  // Initialize all days as disabled
  DAYS.forEach((day) => {
    componentSchedule[day] = {
      enabled: false,
      startTime: null,
      endTime: null,
    };
  });

  let hasAnySchedule = false;

  // Handle array format (v2 API)
  if (Array.isArray(apiSchedule)) {
    apiSchedule.forEach((scheduleItem) => {
      const dayName = DAYS[scheduleItem.dayOfWeek];
      if (dayName && scheduleItem.startTime && scheduleItem.endTime) {
        componentSchedule[dayName] = {
          enabled: true,
          startTime: parseTimeStringToDate(scheduleItem.startTime),
          endTime: parseTimeStringToDate(scheduleItem.endTime),
        };
        hasAnySchedule = true;
      }
    });
  }
  // Handle object format (old API or static data)
  else if (typeof apiSchedule === "object" && apiSchedule !== null) {
    DAYS.forEach((day) => {
      const daySchedule = apiSchedule[day];

      if (daySchedule && daySchedule !== "OFF" && typeof daySchedule === "object") {
        componentSchedule[day] = {
          enabled: true,
          startTime: parseTimeStringToDate(daySchedule.startTime),
          endTime: parseTimeStringToDate(daySchedule.endTime),
        };
        hasAnySchedule = true;
      }
    });
  }

  // If no schedule found and useDefaultForEmpty is true, return initial schedule
  if (!hasAnySchedule && useDefaultForEmpty) {
    return INITIAL_WEEK_SCHEDULE;
  }

  return componentSchedule;
};

const DAY_OF_WEEK_MAP = {
  sunday: 0,
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
};

export const DAYS_ORDER = [
  { key: "monday", label: "MON", dayOfWeek: 1, fullName: "Monday" },
  { key: "tuesday", label: "TUE", dayOfWeek: 2, fullName: "Tuesday" },
  { key: "wednesday", label: "WED", dayOfWeek: 3, fullName: "Wednesday" },
  { key: "thursday", label: "THU", dayOfWeek: 4, fullName: "Thursday" },
  { key: "friday", label: "FRI", dayOfWeek: 5, fullName: "Friday" },
  { key: "saturday", label: "SAT", dayOfWeek: 6, fullName: "Saturday" },
  { key: "sunday", label: "SUN", dayOfWeek: 0, fullName: "Sunday" },
];
/**
 * Convert schedule object from component format to API format (v2)
 * @param {Object} componentSchedule - Schedule with Date objects
 * @returns {Array} Schedule array with dayOfWeek numbers for API v2
 */
export const convertComponentScheduleToAPIFormat = (componentSchedule) => {
  if (!componentSchedule || typeof componentSchedule !== "object") {
    return [];
  }

  const apiSchedule = [];
  const daysOrder = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];

  // Loop through all 7 days to ensure we send all days (working + off days)
  daysOrder.forEach((day) => {
    const schedule = componentSchedule[day];
    const dayOfWeek = DAY_OF_WEEK_MAP[day];

    // If day is enabled and has valid times, send actual times
    if (schedule && schedule.enabled && schedule.startTime && schedule.endTime) {
      apiSchedule.push({
        dayOfWeek,
        startTime: formatTimeToHHMM(schedule.startTime),
        endTime: formatTimeToHHMM(schedule.endTime),
      });
    } else {
      // For off days or days without times, send null values
      apiSchedule.push({
        dayOfWeek,
        startTime: null,
        endTime: null,
      });
    }
  });

  return apiSchedule;
};

/**
 * Get user's timezone
 * @returns {string} Timezone string
 */
export const getUserTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (error) {
    // console.error("Error getting timezone:", error);
    return "UTC";
  }
};

/**
 * Prepare schedule data for API submission
 * @param {Object} weekSchedule - Week schedule with Date objects
 * @param {string} timezone - Optional timezone (defaults to America/Lima)
 * @returns {Object} Complete schedule data for API
 */
export const prepareScheduleDataForAPI = (weekSchedule, timezone = "America/Lima") => {
  if (!weekSchedule) {
    return {
      timezone,
      schedule: [],
    };
  }

  return {
    timezone,
    schedule: convertComponentScheduleToAPIFormat(weekSchedule),
  };
};

/**
 * Validate if schedule has at least one working day
 * @param {Object} weekSchedule - Week schedule object
 * @returns {boolean} True if valid
 */
export const isScheduleValid = (weekSchedule) => {
  if (!weekSchedule || typeof weekSchedule !== "object") {
    return false;
  }
  return Object.values(weekSchedule).some((day) => day && day.enabled && day.startTime && day.endTime);
};

/**
 * Check if current time is within shift range (with 3-hour early clock-in buffer)
 * @param {Date} startTime - Shift start time
 * @param {Date} endTime - Shift end time
 * @returns {boolean} True if current time is in range
 */
export const isCurrentTimeInShiftRange = (startTime, endTime) => {
  if (!startTime || !endTime) return false;

  const now = new Date();
  const currentMinutes = now.getHours() * 60 + now.getMinutes();
  const startMinutes = startTime.getHours() * 60 + startTime.getMinutes();
  const endMinutes = endTime.getHours() * 60 + endTime.getMinutes();
  const threeHoursInMinutes = 3 * 60; // 180 minutes

  // Calculate early clock-in threshold (3 hours before shift start)
  // But cap it at midnight (0 minutes) to avoid previous day confusion
  const earlyClockInMinutes = Math.max(0, startMinutes - threeHoursInMinutes);

  // Handle overnight shifts (end time is on next day)
  if (endMinutes < startMinutes) {
    // Shift spans midnight
    // Allow clock-in from early threshold until shift end (next day)
    return currentMinutes >= earlyClockInMinutes || currentMinutes <= endMinutes;
  }

  // Normal shift within same day
  return currentMinutes >= earlyClockInMinutes && currentMinutes <= endMinutes;
};

/**
 * Get today's schedule from weekly schedule
 * @param {Object} weekSchedule - Complete weekly schedule
 * @returns {Object|null} Today's schedule or null
 */
export const getTodaySchedule = (weekSchedule) => {
  if (!weekSchedule || typeof weekSchedule !== "object") {
    return null;
  }
  const daysOfWeek = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
  const today = new Date().getDay();
  const todayKey = daysOfWeek[today];
  return weekSchedule[todayKey] || null;
};

export const INITIAL_WEEK_SCHEDULE = {
  sunday: { enabled: false, startTime: null, endTime: null },
  monday: { enabled: true, startTime: null, endTime: null },
  tuesday: { enabled: true, startTime: null, endTime: null },
  wednesday: { enabled: true, startTime: null, endTime: null },
  thursday: { enabled: true, startTime: null, endTime: null },
  friday: { enabled: true, startTime: null, endTime: null },
  saturday: { enabled: false, startTime: null, endTime: null },
};

export const MINUTES_PER_HOUR = 60;
export const MINUTES_PER_DAY = 24 * 60; // 1440 minutes
export const THREE_HOURS_IN_MINUTES = 3 * 60; // 180 minutes

/**
 * Convert time to minutes since midnight
 * @param {Date|string} time - Date object or time string (HH:mm)
 * @returns {number} Minutes since midnight
 */
export const timeToMinutes = (time) => {
  if (!time) return 0;

  if (typeof time === "string") {
    const [hours, minutes] = time.split(":").map(Number);
    return hours * MINUTES_PER_HOUR + minutes;
  }

  if (time instanceof Date) {
    return time.getHours() * MINUTES_PER_HOUR + time.getMinutes();
  }

  return 0;
};

/**
 * Convert minutes to hours and minutes
 * @param {number} totalMinutes - Total minutes
 * @returns {Object} Object with hours and minutes properties
 */
export const minutesToTime = (totalMinutes) => {
  const hours = Math.floor(totalMinutes / MINUTES_PER_HOUR);
  const minutes = totalMinutes % MINUTES_PER_HOUR;
  return { hours, minutes };
};

/**
 * Normalize time in minutes (handle overnight by adding day offset)
 * @param {number} timeMinutes - Time in minutes
 * @param {number} referenceMinutes - Reference time (e.g., clock-in time)
 * @returns {number} Normalized time in minutes
 */
export const normalizeTimeMinutes = (timeMinutes, referenceMinutes) => {
  if (timeMinutes < referenceMinutes) {
    return timeMinutes + MINUTES_PER_DAY;
  }
  return timeMinutes;
};

/**
 * Get day of week from date string (0 = Sunday, 6 = Saturday)
 * @param {string} dateString - Date string (YYYY-MM-DD or any valid date format)
 * @returns {number|null} Day of week (0-6) or null if invalid
 */
export const getDayOfWeekFromDate = (dateString) => {
  if (!dateString) return null;
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return null;
  return date.getDay();
};

/**
 * Get schedule for specific day from weekly schedule array
 * @param {Array} weeklySchedule - Array of schedule objects from API
 * @param {number} dayOfWeek - Day of week (0-6)
 * @returns {Object|null} Schedule for the day or null
 */
export const getScheduleForDay = (weeklySchedule, dayOfWeek) => {
  if (!weeklySchedule || !Array.isArray(weeklySchedule)) return null;
  if (dayOfWeek === null || dayOfWeek === undefined) return null;
  return weeklySchedule.find((day) => day.dayOfWeek === dayOfWeek) || null;
};

/**
 * Check if shift is overnight (end time is before start time)
 * @param {string|number} startTime - Start time (HH:mm or minutes)
 * @param {string|number} endTime - End time (HH:mm or minutes)
 * @returns {boolean} True if overnight shift
 */
export const isOvernightShift = (startTime, endTime) => {
  const startMinutes = typeof startTime === "number" ? startTime : timeToMinutes(startTime);
  const endMinutes = typeof endTime === "number" ? endTime : timeToMinutes(endTime);
  return endMinutes < startMinutes;
};

/**
 * Day offset to apply to a clock-in time when serializing it for the API.
 *
 * On an overnight shift the clock-in normally lands on the shift date, but a talent who clocked
 * in after midnight is already on the following day. The earliest a clock-in can legitimately
 * happen is 3 hours before the scheduled start, so anything earlier than that on the clock face
 * has crossed midnight.
 *
 * @param {Date} clockInTime - Clock-in time
 * @param {string} scheduleStart - Scheduled shift start (HH:mm)
 * @param {string} scheduleEnd - Scheduled shift end (HH:mm)
 * @returns {number} 0 to keep the shift date, 1 to move to the next day
 */
export const getClockInDayOffset = (clockInTime, scheduleStart, scheduleEnd) => {
  if (!clockInTime || !scheduleStart || !scheduleEnd) return 0;
  if (!isOvernightShift(scheduleStart, scheduleEnd)) return 0;

  const earliestStartMinutes = timeToMinutes(scheduleStart) - THREE_HOURS_IN_MINUTES;
  return timeToMinutes(clockInTime) < earliestStartMinutes ? 1 : 0;
};

/**
 * Day offset to apply to any timesheet time that follows the clock-in (breaks, nursing breaks
 * and the clock-out) when serializing it for the API.
 *
 * Every one of these events happens chronologically after the clock-in, so a time that sits
 * earlier on the clock face than the clock-in must have crossed midnight. This is the same
 * pivot `normalizeTimeMinutes` and `validateBreakTimes` already use, which keeps the payload
 * consistent with the form validation instead of disagreeing with it.
 *
 * @param {Date} time - The time being serialized
 * @param {Date} clockInTime - Clock-in time for the shift
 * @param {string} scheduleStart - Scheduled shift start (HH:mm)
 * @param {string} scheduleEnd - Scheduled shift end (HH:mm)
 * @returns {number} Number of days to add to the shift date
 */
export const getPayloadDayOffset = (time, clockInTime, scheduleStart, scheduleEnd) => {
  if (!time || !clockInTime) return 0;

  const clockInOffset = getClockInDayOffset(clockInTime, scheduleStart, scheduleEnd);
  const crossedMidnight = timeToMinutes(time) < timeToMinutes(clockInTime) ? 1 : 0;

  return clockInOffset + crossedMidnight;
};

/**
 * Serialize a timesheet time for the update payload as `YYYY-MM-DD HH:mm:ss`, resolving the
 * correct calendar date for overnight shifts.
 *
 * @param {Date} time - The time to serialize
 * @param {Object} [options] - Resolution context
 * @param {Date} [options.baseDate] - The shift date
 * @param {Date} [options.clockInTime] - Clock-in time for the shift
 * @param {string} [options.scheduleStart] - Scheduled shift start (HH:mm)
 * @param {string} [options.scheduleEnd] - Scheduled shift end (HH:mm)
 * @param {boolean} [options.isClockIn] - True when serializing the clock-in itself
 * @param {boolean} [options.includeDate] - False to return only `HH:mm:ss`
 * @returns {string|null} Serialized value, or null when there is no time
 */
export const formatTimesheetDateTimeForPayload = (
  time,
  {
    baseDate = null,
    clockInTime = null,
    scheduleStart = null,
    scheduleEnd = null,
    isClockIn = false,
    includeDate = true,
  } = {}
) => {
  if (!time) return null;

  const hours = String(time.getHours()).padStart(2, "0");
  const minutes = String(time.getMinutes()).padStart(2, "0");
  const timePart = `${hours}:${minutes}:00`;

  if (!includeDate || !baseDate) return timePart;

  const dayOffset = isClockIn
    ? getClockInDayOffset(time, scheduleStart, scheduleEnd)
    : getPayloadDayOffset(time, clockInTime, scheduleStart, scheduleEnd);

  const targetDate = new Date(baseDate);
  targetDate.setDate(targetDate.getDate() + dayOffset);

  const year = targetDate.getFullYear();
  const month = String(targetDate.getMonth() + 1).padStart(2, "0");
  const day = String(targetDate.getDate()).padStart(2, "0");

  return `${year}-${month}-${day} ${timePart}`;
};

/**
 * Validate clock-in time - user can check in up to 3 hours before shift start, but not after shift end
 * @param {Date} clockInTime - Clock-in time to validate
 * @param {string} shiftStartTime - Shift start time (HH:mm)
 * @param {string} shiftEndTime - Shift end time (HH:mm) - optional, used for validation
 * @returns {Object} { isValid: boolean, errorMessage: string }
 */
export const validateClockInTime = (clockInTime, shiftStartTime, shiftEndTime = null) => {
  if (!clockInTime || !shiftStartTime) {
    return { isValid: true, errorMessage: "" };
  }

  const clockInMinutes = timeToMinutes(clockInTime);
  const shiftStartMinutes = timeToMinutes(shiftStartTime);

  // Calculate 3 hours before shift start (early clock-in threshold)
  const earlyClockInMinutes = Math.max(0, shiftStartMinutes - THREE_HOURS_IN_MINUTES);

  // Validate that clock-in is not after shift end time
  if (shiftEndTime) {
    const shiftEndMinutes = timeToMinutes(shiftEndTime);
    const isOvernight = isOvernightShift(shiftStartMinutes, shiftEndMinutes);

    if (isOvernight) {
      // For overnight shifts, the valid clock-in window is:
      // - From earlyClockInMinutes to end of day (23:59)
      // - OR from start of day (00:00) to shiftEndMinutes
      // Invalid if clock-in is in the "dead zone" (after shift end but before early clock-in window)
      if (clockInMinutes > shiftEndMinutes && clockInMinutes < earlyClockInMinutes) {
        return {
          isValid: false,
          errorMessage: "Clock in time must be no more than 3 hours before shift start time",
        };
      }
    } else {
      // For regular (non-overnight) shifts
      // Check early clock-in limit
      if (clockInMinutes < earlyClockInMinutes) {
        return {
          isValid: false,
          errorMessage: "Clock in time must be no more than 3 hours before shift start time",
        };
      }

      // Check that clock-in is not after shift end
      if (clockInMinutes > shiftEndMinutes) {
        return {
          isValid: false,
          errorMessage: "Clock in time cannot be after shift end time",
        };
      }
    }
  } else if (clockInMinutes < earlyClockInMinutes) {
    // No shift end time provided, only validate early clock-in limit
    return {
      isValid: false,
      errorMessage: "Clock in time must be no more than 3 hours before shift start time",
    };
  }

  return { isValid: true, errorMessage: "" };
};

/**
 * Validate clock-out time - only validates that clock-out is after clock-in (no time limit)
 * @param {Date} clockOutTime - Clock-out time to validate
 * @param {Date} clockInTime - Clock-in time (for reference)
 * @param {string} shiftStartTime - Shift start time (HH:mm) - unused but kept for backward compatibility
 * @param {string} shiftEndTime - Shift end time (HH:mm) - unused but kept for backward compatibility
 * @returns {Object} { isValid: boolean, errorMessage: string }
 */
// eslint-disable-next-line no-unused-vars
export const validateClockOutTime = (clockOutTime, clockInTime, shiftStartTime, shiftEndTime) => {
  if (!clockOutTime) {
    return { isValid: true, errorMessage: "" };
  }

  if (!clockInTime) {
    return { isValid: true, errorMessage: "" };
  }

  const clockInMinutes = timeToMinutes(clockInTime);
  const clockOutMinutes = timeToMinutes(clockOutTime);

  // Normalize clock-out time (handle overnight)
  const normalizedClockOutMinutes = normalizeTimeMinutes(clockOutMinutes, clockInMinutes);

  // Only validate that clock-out is after clock-in
  // No time limit - user can clock out anytime after clock-in
  if (normalizedClockOutMinutes <= clockInMinutes) {
    return {
      isValid: false,
      errorMessage: "Clock out time must be after clock in time",
    };
  }

  return { isValid: true, errorMessage: "" };
};

/**
 * Validate break times are within clock-in/out window and shift ±3 hours
 * @param {Array} breakSlots - Array of break slots with startTime and endTime
 * @param {Date} clockInTime - Clock-in time
 * @param {Date} clockOutTime - Clock-out time (optional)
 * @param {string} shiftStartTime - Shift start time (HH:mm)
 * @param {string} shiftEndTime - Shift end time (HH:mm)
 * @returns {Object} { isValid: boolean, errorMessage: string }
 */
export const validateBreakTimes = (breakSlots, clockInTime, clockOutTime, shiftStartTime, shiftEndTime) => {
  if (!clockInTime) {
    return { isValid: true, errorMessage: "" };
  }

  const validSlots = breakSlots.filter((slot) => slot.startTime && slot.endTime);
  if (validSlots.length === 0) {
    return { isValid: true, errorMessage: "" };
  }

  const clockInMinutes = timeToMinutes(clockInTime);
  const clockOutMinutes = clockOutTime ? normalizeTimeMinutes(timeToMinutes(clockOutTime), clockInMinutes) : null;

  // Get shift boundaries with ±3 hours buffer
  let shiftStartMinutes = null;
  let shiftEndMinutes = null;

  if (shiftStartTime && shiftEndTime) {
    shiftStartMinutes = timeToMinutes(shiftStartTime);
    shiftEndMinutes = timeToMinutes(shiftEndTime);

    // Apply ±3 hours buffer
    shiftStartMinutes = Math.max(0, shiftStartMinutes - THREE_HOURS_IN_MINUTES);
    shiftEndMinutes += THREE_HOURS_IN_MINUTES;

    // Handle overnight shifts
    if (isOvernightShift(timeToMinutes(shiftStartTime), timeToMinutes(shiftEndTime))) {
      shiftEndMinutes += MINUTES_PER_DAY;
    }
  }

  for (let i = 0; i < validSlots.length; i += 1) {
    const slot = validSlots[i];
    let breakStartMinutes = timeToMinutes(slot.startTime);
    let breakEndMinutes = timeToMinutes(slot.endTime);

    // Normalize break times (handle overnight breaks)
    breakEndMinutes = normalizeTimeMinutes(breakEndMinutes, breakStartMinutes);
    if (breakStartMinutes < clockInMinutes) {
      breakStartMinutes += MINUTES_PER_DAY;
      breakEndMinutes += MINUTES_PER_DAY;
    }

    // Validate break is after clock-in
    if (breakStartMinutes < clockInMinutes) {
      return {
        isValid: false,
        errorMessage: "Break must start after clock in",
      };
    }

    // Validate break is before clock-out (if clock-out exists)
    if (clockOutMinutes !== null && breakEndMinutes > clockOutMinutes) {
      return {
        isValid: false,
        errorMessage: "Break must end before clock out",
      };
    }

    // Validate break is within shift time ±3 hours
    if (shiftStartMinutes !== null && shiftEndMinutes !== null) {
      if (breakStartMinutes < shiftStartMinutes || breakEndMinutes > shiftEndMinutes) {
        return {
          isValid: false,
          errorMessage: "Break must be within shift time ±3 hours window",
        };
      }
    }
  }

  return { isValid: true, errorMessage: "" };
};

/**
 * Validate nursing break times and total duration
 * @param {Array} nursingSlots - Array of nursing break slots
 * @param {Date} clockInTime - Clock-in time
 * @param {Date} clockOutTime - Clock-out time (optional)
 * @param {string} shiftStartTime - Shift start time (HH:mm)
 * @param {string} shiftEndTime - Shift end time (HH:mm)
 * @param {number} maxDurationMinutes - Maximum allowed duration (default: 60 minutes)
 * @returns {Object} { isValid: boolean, errorMessage: string }
 */
export const validateNursingBreakTimes = (
  nursingSlots,
  clockInTime,
  clockOutTime,
  shiftStartTime,
  shiftEndTime,
  maxDurationMinutes = 60
) => {
  if (!clockInTime) {
    return { isValid: true, errorMessage: "" };
  }

  const validSlots = nursingSlots.filter((slot) => slot.startTime && slot.endTime);
  if (validSlots.length === 0) {
    return { isValid: true, errorMessage: "" };
  }

  // Validate total nursing break duration
  const totalNursingMinutes = validSlots.reduce((total, slot) => {
    const startMinutes = timeToMinutes(slot.startTime);
    let endMinutes = timeToMinutes(slot.endTime);
    endMinutes = normalizeTimeMinutes(endMinutes, startMinutes);
    return total + (endMinutes - startMinutes);
  }, 0);

  if (totalNursingMinutes > maxDurationMinutes) {
    return {
      isValid: false,
      errorMessage: `Total nursing break duration cannot exceed ${maxDurationMinutes} minutes (1 hour)`,
    };
  }

  // Use same validation as regular breaks
  return validateBreakTimes(nursingSlots, clockInTime, clockOutTime, shiftStartTime, shiftEndTime);
};

/**
 * Calculate duration between two times
 * @param {Date} startTime - Start time
 * @param {Date} endTime - End time
 * @returns {string} Duration in "X hr Y min" format
 */
export const calculateDuration = (startTime, endTime) => {
  if (!startTime || !endTime) return "0 hr 0 min";

  const startMinutes = timeToMinutes(startTime);
  const endMinutes = timeToMinutes(endTime);

  let totalMinutes;
  if (endMinutes < startMinutes) {
    // Overnight
    totalMinutes = MINUTES_PER_DAY - startMinutes + endMinutes;
  } else {
    totalMinutes = endMinutes - startMinutes;
  }

  const { hours, minutes } = minutesToTime(totalMinutes);
  return `${hours} hr ${minutes} min`;
};

/**
 * Calculate total duration for multiple time slots
 * @param {Array} slots - Array of slots with startTime and endTime
 * @returns {string} Total duration in "X hr Y min" or "Y min" format
 */
export const calculateTotalDuration = (slots) => {
  let totalMinutes = 0;

  slots.forEach((slot) => {
    if (slot.startTime && slot.endTime) {
      const startMinutes = timeToMinutes(slot.startTime);
      const endMinutes = timeToMinutes(slot.endTime);

      let duration;
      if (endMinutes < startMinutes) {
        duration = MINUTES_PER_DAY - startMinutes + endMinutes;
      } else {
        duration = endMinutes - startMinutes;
      }

      totalMinutes += duration;
    }
  });

  const { hours, minutes } = minutesToTime(totalMinutes);

  if (hours > 0) {
    return `${hours} hr ${minutes} min`;
  }
  return `${minutes} min`;
};

/**
 * Get date label for time display (shows "Next Day" or formatted date for overnight scenarios)
 * Used for displaying date labels on time fields in timesheets
 * @param {Date} startTime - Start time
 * @param {Date} endTime - End time
 * @param {Date} baseDate - Base date for the shift
 * @param {string} scheduleStart - Scheduled start time (HH:mm)
 * @param {string} scheduleEnd - Scheduled end time (HH:mm)
 * @returns {Object} { showLabel: boolean, text: string }
 */
export const getDateLabelForTime = (startTime, endTime, baseDate = null, scheduleStart = null, scheduleEnd = null) => {
  if (!endTime) {
    return { showLabel: false, text: "" };
  }

  if (!startTime) {
    const options = { day: "numeric", month: "short", year: "numeric" };
    const displayDate = baseDate || endTime;
    return { showLabel: true, text: displayDate.toLocaleDateString("en-US", options) };
  }

  // Check if startTime and endTime already have actual calendar dates set
  if (baseDate) {
    const startDateOnly = new Date(startTime);
    startDateOnly.setHours(0, 0, 0, 0);
    const endDateOnly = new Date(endTime);
    endDateOnly.setHours(0, 0, 0, 0);
    const baseDateOnly = new Date(baseDate);
    baseDateOnly.setHours(0, 0, 0, 0);

    const startMatchesBase = startDateOnly.getTime() === baseDateOnly.getTime();
    const startIsNextDay = startDateOnly.getTime() === baseDateOnly.getTime() + 86400000;

    if (startMatchesBase || startIsNextDay) {
      if (startDateOnly.getTime() === endDateOnly.getTime()) {
        const options = { day: "numeric", month: "short", year: "numeric" };
        return { showLabel: true, text: endTime.toLocaleDateString("en-US", options) };
      }

      const dayDifference = (endDateOnly.getTime() - startDateOnly.getTime()) / (1000 * 60 * 60 * 24);
      if (dayDifference === 1) {
        return { showLabel: true, text: "Next Day" };
      }
    }
  }

  if (!baseDate || !scheduleStart || !scheduleEnd) {
    const startMinutes = timeToMinutes(startTime);
    const endMinutes = timeToMinutes(endTime);

    if (endMinutes < startMinutes) {
      return { showLabel: true, text: "Next Day" };
    }

    const options = { day: "numeric", month: "short", year: "numeric" };
    const displayDate = baseDate || endTime;
    return { showLabel: true, text: displayDate.toLocaleDateString("en-US", options) };
  }

  const startMinutes = timeToMinutes(startTime);
  const endMinutes = timeToMinutes(endTime);
  const scheduleStartMinutes = timeToMinutes(scheduleStart);
  const scheduleEndMinutes = timeToMinutes(scheduleEnd);

  const isOvernight = isOvernightShift(scheduleStartMinutes, scheduleEndMinutes);
  const isOvernightClockInOut = endMinutes < startMinutes;
  const isClockInAlreadyNextDay = isOvernight && startMinutes < scheduleStartMinutes;

  const options = { day: "numeric", month: "short", year: "numeric" };

  if (isOvernightClockInOut) {
    if (isClockInAlreadyNextDay) {
      const clockOutDate = new Date(baseDate);
      clockOutDate.setDate(clockOutDate.getDate() + 1);
      return { showLabel: true, text: clockOutDate.toLocaleDateString("en-US", options) };
    }
    return { showLabel: true, text: "Next Day" };
  }

  return { showLabel: true, text: baseDate.toLocaleDateString("en-US", options) };
};

export const TIMEZONE_DROPDOWN_OPTIONS = [
  {
    label: "(GMT-5) America/Lima",
    value: "America/Lima",
  },
  {
    label: "(GMT+5) Asia/Karachi",
    value: "Asia/Karachi",
  },
];

const COUNTRY_TIMEZONE_MAP = {
  Peru: "America/Lima",
  Pakistan: "Asia/Karachi",
};

export const getDefaultTimezoneForCountry = (timeoffCountry) => {
  if (!timeoffCountry) return TIMEZONE_DROPDOWN_OPTIONS[0].value;
  const mapped = COUNTRY_TIMEZONE_MAP[timeoffCountry];
  if (mapped) return mapped;
  return TIMEZONE_DROPDOWN_OPTIONS[0].value;
};

export const formatDateWithoutTimeZone = (dateString) => {
  if (!dateString) return "-";
  const [year, month, day] = dateString.split("-");

  const date = new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

export const getDateWithoutTimeZone = (dateString) => {
  if (!dateString) return null;
  const [year, month, day] = dateString.split("-");

  return new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));
};
